<script setup lang="ts">
import { AuthMode } from '~/types/interfaces'
import { useGoogleAuth } from '~/composables/api/useGoogleAuth'
import { useAuthFlow } from './auth/useAuthFlow'

defineOptions({ name: 'AppAuth' })

const props = defineProps({
  mode: {
    type: String as () => AuthMode,
    required: true,
  },
})

const modeRef = toRef(props, 'mode')

const {
  username,
  password,
  passwordConfirm,
  code,
  agreed,
  visible,
  loading,
  modeParams,
  isOfflineAvailable,
  authChallengeSession,
  onSubmit,
} = useAuthFlow(modeRef)

const { isGoogleLoginAvailable, buildGoogleAuthUrl } = useGoogleAuth()

const showGoogleButton = computed(() => {
  return isGoogleLoginAvailable.value
    && (props.mode === AuthMode.SIGNIN || props.mode === AuthMode.SIGNUP)
})

const onGoogleSignIn = () => {
  window.location.href = buildGoogleAuthUrl()
}
</script>

<template>
  <v-card
    class="mx-auto mt-8 pa-4 signin-base"
    max-width="640"
    rounded="lg"
  >
    <div class="ma-4">
      <img
        alt="Code & Magic logo"
        src="@/assets/images/code_and_magic_black_textlogo.svg"
      >
    </div>

    <AuthForm
      v-model:username="username"
      v-model:password="password"
      v-model:password-confirm="passwordConfirm"
      v-model:code="code"
      v-model:visible="visible"
      v-model:agreed="agreed"
      :mode="mode"
      :mode-params="modeParams"
      :loading="loading"
      :auth-challenge-session="authChallengeSession"
      @submit="onSubmit"
    >
      <template
        v-if="showGoogleButton"
        #social-login
      >
        <v-btn
          class="mb-2"
          variant="outlined"
          block
          size="large"
          @click="onGoogleSignIn"
        >
          <template #prepend>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              width="20"
              height="20"
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
          </template>
          Google でサインイン
        </v-btn>
        <div class="d-flex align-center my-4">
          <v-divider />
          <span
            class="mx-3 text-caption text-medium-emphasis"
            style="white-space: nowrap;"
          >または</span>
          <v-divider />
        </div>
      </template>
    </AuthForm>

    <v-sheet
      class="align-center text-center my-2 mt-5"
      width="100%"
      height="auto"
      color="transparent"
    >
      <v-btn
        variant="text"
        color="primary"
        height="auto"
        to="/"
      >
        ホームに戻る
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="isOfflineAvailable"
        variant="text"
        color="#d8d8d8"
        height="auto"
        to="/offline"
      >
        オフラインモード
      </v-btn>
    </v-sheet>
  </v-card>
</template>

<style scoped>
.signin-base {
  background-color: lightgray;
}
</style>
